import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, CheckCircle2, UserPlus } from "lucide-react";
import { COUNTRIES, INDIA_STATES, getCitiesForState } from "@/lib/locations";

const GENDERS = ["Male", "Female", "Other", "Prefer not to say"];
const MARITAL = ["Single", "Married", "Divorced", "Widowed", "Other"];

const initial = {
  passing_year: "",
  branch: "",
  first_name: "",
  middle_name: "",
  last_name: "",
  gender: "",
  marital_status: "",
  date_of_birth: "",
  current_address: "",
  country: "India",
  state: "",
  state_other: "",
  city: "",
  city_other: "",
  zipcode: "",
  mobile_no: "",
  email: "",
  linkedin_url: "",
  current_profession: "",
};

// Themed form — accent classes provided by host institute layout so each campus styles it natively.
export default function AlumniRegistrationForm({
  slug,
  themeAccent = "bg-[#234333] hover:bg-[#1b3326] text-white",
  themeBorder = "border-[#234333]/30",
  themeLabel = "text-[#234333]",
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(initial);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/public/website/${slug}`);
        setData(res.data);
      } catch {
        toast.error("Could not load institute details");
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const yearRange = useMemo(() => {
    const start = Number(data?.institute?.year_of_establishment) || new Date().getFullYear() - 30;
    const end = new Date().getFullYear();
    const out = [];
    for (let y = end; y >= start; y--) out.push(y);
    return out;
  }, [data]);

  const branches = useMemo(() => {
    const set = new Set();
    (data?.departments || []).forEach((d) => d?.name && set.add(d.name));
    (data?.academics || []).forEach((a) => a?.course_name && set.add(a.course_name));
    return Array.from(set).sort();
  }, [data]);

  const cityOptions = useMemo(
    () => getCitiesForState(form.country, form.state),
    [form.country, form.state],
  );

  const handlePhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\/jpe?g$/.test(f.type)) {
      toast.error("Photo must be a JPG file");
      return;
    }
    if (f.size > 100 * 1024) {
      toast.error("Photo must be 100 KB or smaller");
      return;
    }
    setPhotoFile(f);
    setPhotoPreview(URL.createObjectURL(f));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.first_name.trim() || !form.last_name.trim() || !form.mobile_no.trim() || !form.passing_year) {
      toast.error("Please fill in first name, last name, mobile and passing year");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("institute_slug", slug);
      const state = form.state === "Other" ? form.state_other : form.state;
      const city = form.city === "Other" ? form.city_other : form.city;
      const payload = {
        ...form,
        state,
        city,
      };
      delete payload.state_other;
      delete payload.city_other;
      Object.entries(payload).forEach(([k, v]) => {
        if (v !== null && v !== undefined && v !== "") fd.append(k, v);
      });
      if (photoFile) fd.append("photo", photoFile);
      await api.post("/public/alumni-registration", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="py-24 text-center text-slate-500" data-testid="alumni-reg-loading">
        <Loader2 className="animate-spin inline" /> Loading…
      </div>
    );
  }

  if (submitted) {
    return (
      <div
        className="max-w-2xl mx-auto my-16 p-10 border rounded-md bg-white text-center"
        data-testid="alumni-reg-success"
      >
        <CheckCircle2 size={48} className="mx-auto text-green-600 mb-3" />
        <h2 className="font-serif text-3xl mb-3">Thank you!</h2>
        <p className="text-slate-600">
          Your registration has been received. The institute will review your details and
          confirm your profile shortly.
        </p>
      </div>
    );
  }

  const inputCls =
    `w-full bg-white border ${themeBorder} text-slate-800 rounded-sm px-3 py-2.5 text-sm ` +
    `focus:outline-none focus:ring-1 focus:ring-current`;
  const labelCls = `text-[10px] tracking-[0.2em] uppercase ${themeLabel} font-semibold mb-2 block`;

  return (
    <form onSubmit={submit} className="max-w-4xl mx-auto py-12 px-4" data-testid="alumni-reg-form">
      <div className="text-center mb-10">
        <UserPlus size={32} className={`mx-auto mb-3 ${themeLabel}`} />
        <h1 className="font-serif text-4xl text-slate-900">Alumni Registration</h1>
        <p className="text-slate-600 mt-2 text-sm">
          {data?.institute?.name ? `${data.institute.name} — ` : ""}
          We&apos;d love to stay connected. Please fill in your details below.
        </p>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-6 md:p-8 grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className={labelCls}>Passing Year *</label>
          <select
            data-testid="ar-passing-year"
            className={inputCls}
            value={form.passing_year}
            onChange={(e) => set("passing_year", e.target.value)}
            required
          >
            <option value="">— Select —</option>
            {yearRange.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>Branch</label>
          <select
            data-testid="ar-branch"
            className={inputCls}
            value={form.branch}
            onChange={(e) => set("branch", e.target.value)}
          >
            <option value="">— Select —</option>
            {branches.map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelCls}>First Name *</label>
          <input data-testid="ar-fname" className={inputCls} value={form.first_name} onChange={(e) => set("first_name", e.target.value)} required />
        </div>
        <div>
          <label className={labelCls}>Middle Name</label>
          <input data-testid="ar-mname" className={inputCls} value={form.middle_name} onChange={(e) => set("middle_name", e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Last Name *</label>
          <input data-testid="ar-lname" className={inputCls} value={form.last_name} onChange={(e) => set("last_name", e.target.value)} required />
        </div>
        <div>
          <label className={labelCls}>Gender</label>
          <select data-testid="ar-gender" className={inputCls} value={form.gender} onChange={(e) => set("gender", e.target.value)}>
            <option value="">— Select —</option>
            {GENDERS.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>Marital Status</label>
          <select data-testid="ar-marital" className={inputCls} value={form.marital_status} onChange={(e) => set("marital_status", e.target.value)}>
            <option value="">— Select —</option>
            {MARITAL.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>Date of Birth</label>
          <input type="date" data-testid="ar-dob" className={inputCls} value={form.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} />
        </div>

        <div className="md:col-span-2">
          <label className={labelCls}>Current Address</label>
          <textarea data-testid="ar-address" rows={2} className={inputCls} value={form.current_address} onChange={(e) => set("current_address", e.target.value)} />
        </div>

        <div>
          <label className={labelCls}>Country</label>
          <select
            data-testid="ar-country"
            className={inputCls}
            value={form.country}
            onChange={(e) => {
              set("country", e.target.value);
              set("state", "");
              set("city", "");
            }}
          >
            {COUNTRIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>State / Province</label>
          {form.country === "India" ? (
            <>
              <select
                data-testid="ar-state"
                className={inputCls}
                value={form.state}
                onChange={(e) => {
                  set("state", e.target.value);
                  set("city", "");
                }}
              >
                <option value="">— Select —</option>
                {INDIA_STATES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
                <option value="Other">Other (specify)</option>
              </select>
              {form.state === "Other" ? (
                <input
                  data-testid="ar-state-other"
                  placeholder="Type state name"
                  className={inputCls + " mt-2"}
                  value={form.state_other}
                  onChange={(e) => set("state_other", e.target.value)}
                />
              ) : null}
            </>
          ) : (
            <input
              data-testid="ar-state-text"
              className={inputCls}
              value={form.state}
              onChange={(e) => set("state", e.target.value)}
            />
          )}
        </div>
        <div>
          <label className={labelCls}>City</label>
          {cityOptions.length > 0 ? (
            <>
              <select
                data-testid="ar-city"
                className={inputCls}
                value={form.city}
                onChange={(e) => set("city", e.target.value)}
              >
                <option value="">— Select —</option>
                {cityOptions.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              {form.city === "Other" ? (
                <input
                  data-testid="ar-city-other"
                  placeholder="Type city name"
                  className={inputCls + " mt-2"}
                  value={form.city_other}
                  onChange={(e) => set("city_other", e.target.value)}
                />
              ) : null}
            </>
          ) : (
            <input
              data-testid="ar-city-text"
              className={inputCls}
              value={form.city}
              onChange={(e) => set("city", e.target.value)}
            />
          )}
        </div>
        <div>
          <label className={labelCls}>Zipcode</label>
          <input data-testid="ar-zip" className={inputCls} value={form.zipcode} onChange={(e) => set("zipcode", e.target.value.replace(/[^0-9A-Za-z\- ]/g, ""))} maxLength={12} />
        </div>

        <div>
          <label className={labelCls}>Mobile No *</label>
          <input
            data-testid="ar-mobile"
            className={inputCls}
            value={form.mobile_no}
            onChange={(e) => set("mobile_no", e.target.value.replace(/[^0-9+\- ]/g, ""))}
            placeholder="+91 …"
            required
          />
        </div>
        <div>
          <label className={labelCls}>Email</label>
          <input type="email" data-testid="ar-email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} />
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>LinkedIn Profile URL</label>
          <input data-testid="ar-linkedin" className={inputCls} placeholder="https://www.linkedin.com/in/…" value={form.linkedin_url} onChange={(e) => set("linkedin_url", e.target.value)} />
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Current Profession</label>
          <input data-testid="ar-profession" className={inputCls} placeholder="e.g. Software Engineer at Infosys" value={form.current_profession} onChange={(e) => set("current_profession", e.target.value)} />
        </div>

        <div className="md:col-span-2">
          <label className={labelCls}>Photo · JPG only · max 100 KB</label>
          <div className="flex items-center gap-4">
            {photoPreview ? (
              <img src={photoPreview} alt="preview" className="w-20 h-20 object-cover rounded border border-slate-200" />
            ) : (
              <div className="w-20 h-20 rounded border border-dashed border-slate-300 flex items-center justify-center text-slate-400 text-[10px]">
                No photo
              </div>
            )}
            <input
              type="file"
              accept="image/jpeg,image/jpg"
              data-testid="ar-photo"
              onChange={handlePhoto}
              className="text-sm"
            />
          </div>
        </div>
      </div>

      <div className="text-center mt-8">
        <button
          type="submit"
          disabled={submitting}
          data-testid="ar-submit"
          className={`inline-flex items-center gap-2 ${themeAccent} text-sm font-semibold tracking-wide uppercase px-8 py-3 rounded-sm disabled:opacity-50`}
        >
          {submitting ? <Loader2 size={16} className="animate-spin" /> : <UserPlus size={16} />}
          {submitting ? "Submitting…" : "Submit Registration"}
        </button>
      </div>
    </form>
  );
}
